import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import { toast } from 'react-toastify'
import { updateTask } from 'services/taskService'

const OPTIONS = [
  { value: 'low', label: 'Low', color: '#15803d', bg: '#dcfce7' },
  { value: 'medium', label: 'Medium', color: '#d97706', bg: '#fef3c7' },
  { value: 'high', label: 'High', color: '#c2410c', bg: '#ffedd5' },
  { value: 'critical', label: 'Critical', color: '#b00020', bg: '#fdecea' },
]

const TaskPriorityQuickEdit = ({ task, onSaved }) => {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState(null)
  const [busy, setBusy] = useState(false)
  const btnRef = useRef(null)
  const popRef = useRef(null)

  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      const width = 160
      const left = Math.max(12, Math.min(r.left, window.innerWidth - width - 12))
      setCoords({ top: Math.min(r.bottom + 6, window.innerHeight - 220), left })
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onClick = e => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false)
    }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const pick = async value => {
    if (value === task.priority) { setOpen(false); return }
    setBusy(true)
    try {
      await updateTask({ id: task.id, priority: value })
      toast.success('Priority updated.')
      setOpen(false)
      onSaved?.()
    } catch (e) {
      toast.error('Could not update the priority. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (!task?.id) return null

  const current = OPTIONS.find(o => o.value === task.priority)

  return (
    <>
      <Trigger
        ref={btnRef}
        type='button'
        title='Change priority'
        $color={current?.color}
        $bg={current?.bg}
        onClick={e => { e.stopPropagation(); setOpen(o => !o) }}
      >
        <FlagOutlinedIcon style={{ fontSize: 13 }} />
        {current ? current.label : 'Set priority'}
      </Trigger>

      {open && coords && createPortal(
        <Pop ref={popRef} role='dialog' aria-label='Change priority' style={{ top: coords.top, left: coords.left }} onClick={e => e.stopPropagation()}>
          {OPTIONS.map(o => (
            <Opt
              key={o.value}
              type='button'
              $color={o.color}
              $bg={o.bg}
              $active={o.value === task.priority}
              disabled={busy}
              onClick={() => pick(o.value)}
            >
              <Dot $color={o.color} />
              {o.label}
            </Opt>
          ))}
        </Pop>,
        document.body
      )}
    </>
  )
}

export default TaskPriorityQuickEdit

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px dashed ${({ $color }) => ($color ? 'transparent' : '#cfd4dd')};
  background: ${({ $bg }) => $bg || '#fff'};
  color: ${({ $color }) => $color || '#6b7280'};
  font: inherit;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
  transition: filter 150ms ease;
  &:hover { filter: brightness(0.96); }
`
const Pop = styled.div`
  position: fixed;
  z-index: 100010;
  width: 160px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 8px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  box-shadow: 0 18px 44px -22px rgba(12, 18, 41, 0.4);
`
const Opt = styled.button`
  font: inherit;
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border: 1px solid ${({ $active, $color }) => ($active ? $color : 'transparent')};
  border-radius: 8px;
  background: ${({ $active, $bg }) => ($active ? $bg : 'transparent')};
  color: ${({ $color }) => $color};
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: ${({ $bg }) => $bg}; }
  &:disabled { opacity: 0.6; cursor: default; }
`
const Dot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex: 0 0 auto;
`
